import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-200 ease-in-out outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "rounded-md bg-primary text-primary-foreground shadow-[inset_0_1px_0_rgb(255_255_255/0.18),0_1px_2px_rgb(69_24_146/0.25)] hover:bg-[var(--primary-hover)] active:bg-[var(--primary-active)]",
        destructive:
          "rounded-md bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "rounded-md border border-border bg-card text-foreground hover:border-border hover:bg-accent",
        secondary:
          "rounded-md bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "rounded-md text-foreground hover:bg-foreground/[0.06] hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline hover:scale-100",
        onDark:
          "rounded-xl bg-white text-[#451892] shadow-sm hover:bg-[#f3f4f6] focus-visible:ring-offset-[#451892]",
        onDarkOutline:
          "rounded-xl border-2 border-white/50 bg-transparent text-white hover:bg-white/10 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#451892]",
        transparent:
          "rounded-xl bg-transparent text-[#451892] hover:bg-transparent hover:text-[#391578] focus-visible:ring-offset-[#451892]",
        transparentOutline:
          "rounded-xl border-2 border-[#451892] bg-transparent text-[#451892] hover:bg-transparent hover:text-[#391578] focus-visible:ring-offset-[#451892]",
        transparentSecondary:
          "rounded-xl bg-transparent text-[#451892] hover:bg-transparent hover:text-[#391578] focus-visible:ring-offset-[#451892]",
      },
      size: {
        default: "h-11 px-5 py-2.5 has-[>svg]:px-4",
        xs: "h-7 gap-1 rounded-lg px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: "h-9 rounded-md gap-1.5 px-3 has-[>svg]:px-3 text-[13px]",
        lg: "h-12 rounded-md px-7 has-[>svg]:px-5",
        xl: "h-12 gap-2 rounded-xl px-7 text-base has-[>svg]:px-5 [&_svg:not([class*='size-'])]:size-5",
        icon: "size-11",
        "icon-xs": "size-7 rounded-lg [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-9 rounded-lg",
        "icon-lg": "size-11 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Button, buttonVariants }
